import { Link } from 'gatsby';
import React, { MouseEventHandler, memo, useMemo } from 'react';
import { LinkTarget } from '../../interfaces/types/LinkTarget.type';
import { rewriteSlug } from '../../utils/RewriteSlug/rewriteSlug.util';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';

export interface CustomLinkProps {
  url: string;
  label?: string;
  target?: LinkTarget;
  children?: React.ReactNode;
  className?: string;
  anchor?: string;
  isDisabled?: boolean;
  onMouseEnter?: MouseEventHandler<HTMLAnchorElement> | undefined;
  onMouseLeave?: MouseEventHandler<HTMLAnchorElement> | undefined;
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
}

const CustomLink: React.FC<CustomLinkProps> = ({
  url,
  label,
  target,
  className,
  children,
  anchor = undefined,
  isDisabled = false,
  onMouseEnter = undefined,
  onMouseLeave = undefined,
  onClick = undefined,
}) => {
  const isEmail = (url: string): boolean => {
    return /^\S+@\S+\.\S+$/.test(url) || url?.startsWith('mailto:');
  };

  const isTelephone = (url: string): boolean => {
    return url?.startsWith('tel:');
  };

  const isOutboundLink = (url: string): boolean => {
    return /^https?:\/\//.test(url);
  };
  const path: string = useMemo(() => {
    if (isEmail(url)) {
      return url?.startsWith('mailto:') ? url : `mailto:${url}`;
    } else if (anchor) {
      return `${url}#${anchor}`;
    } else {
      return url;
    }
  }, [url, anchor]);
  const linkTarget: string = target || isOutboundLink(url) ? '_blank' : '';

  const handleOutboundLinkClickDataLayerEvent = (
    url: string,
    label: string
  ): void => {
    pushToDataLayer({
      event: 'third_party_cta',
      data: {
        thirdparty_cta_url: url,
        thirdparty_cta_text: label,
      },
    });
  };

  if (
    isEmail(url) ||
    isTelephone(url) ||
    isOutboundLink(
      url.replace(
        process.env.GATSBY_WEBSITE_URL ||
          'https://myhomemoveconveyancing.co.uk',
        ''
      )
    )
  ) {
    return (
      <a
        href={path}
        target={linkTarget}
        rel="nofollow"
        aria-label={label}
        className={`custom-link ${className} ${
          isDisabled && 'pointer-events-none'
        }`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          onClick;
          if (isOutboundLink(url)) {
            handleOutboundLinkClickDataLayerEvent(url, label || '');
          }
        }}
      >
        {children}
      </a>
    );
  }

  const internalPath = useMemo(() => {
    let internalPath: string = rewriteSlug(url).replace(
      process.env.GATSBY_WEBSITE_URL || 'https://myhomemoveconveyancing.co.uk',
      ''
    );
    if (!internalPath?.startsWith('/')) internalPath = `/${internalPath}`;

    if (
      internalPath.charAt(internalPath.length - 1) != '/' &&
      !internalPath.includes('?')
    ) {
      internalPath = internalPath + '/';
      internalPath = internalPath.toLowerCase();
    }

    if (anchor) {
      internalPath = internalPath.substring(0, internalPath.length - 1);
      internalPath = `${internalPath}#${anchor}`;
    }

    return internalPath;
  }, [url]);

  const isContactUsButton =
    label && label?.toString()?.toLowerCase() === 'contact us';
  const runDataLayerEvent = () => {
    pushToDataLayer({
      event: 'contact_us',
      data: {
        contact_source: location.href,
      },
    });
  };

  return (
    <Link
      to={internalPath}
      aria-label={label}
      className={`custom-link ${
        isDisabled && 'pointer-events-none'
      } ${className}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={isContactUsButton ? runDataLayerEvent : onClick}
      state={{
        prevPath: typeof window !== 'undefined' && window.location.pathname,
      }}
    >
      {children}
    </Link>
  );
};

export default memo(CustomLink);
